import React, { useEffect, useState } from 'react';
import Topbar from '../components/layout/Topbar';
import { useAuth } from '../contexts/AuthContext';
import { useCountry } from '../contexts/CountryContext';
import { refApi, signalApi, formatApiError } from '../lib/api';
import { SIGNAL_PRIORITIES, SIGNAL_STATUSES } from '../lib/countryConfig';

export default function DashboardAdmin() {
  const { profile } = useAuth();
  const { country, countryCode, t } = useCountry();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [communeNom, setCommuneNom] = useState('—');
  const [infras, setInfras] = useState([]);
  const [signaux, setSignaux] = useState([]);
  const [filter, setFilter] = useState('all');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const cId = profile?.commune_id;
      if (cId) {
        const { data: coms } = await refApi.communes();
        setCommuneNom(coms.find((c) => c.id === cId)?.nom || '—');
      }
      const [infraRes, sigRes] = await Promise.all([
        refApi.infrastructures(countryCode, cId || undefined),
        signalApi.list({ country_mode: countryCode, commune_id: cId || undefined, limit: 100 }),
      ]);
      setInfras(infraRes.data || []);
      setSignaux(sigRes.data || []);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [profile?.commune_id, countryCode]);

  const filteredSig = filter === 'all' ? signaux : signaux.filter((s) => (filter === 'open' ? s.statut !== 'resolu' : s.statut === filter));
  const total = infras.length;
  const ok = infras.filter((i) => i.statut === 'operationnel').length;
  const pctOk = total ? Math.round((ok / total) * 100) : 0;
  const alertes = signaux.filter((s) => s.statut !== 'resolu').length;

  const updateStatut = async (id, newStatut) => {
    try {
      await signalApi.update(id, { statut: newStatut });
      setSignaux((prev) => prev.map((s) => (s.id === id ? { ...s, statut: newStatut } : s)));
    } catch (e) { setError(formatApiError(e)); }
  };

  return (
    <div className="h-screen flex flex-col bg-[#F4F6F9]" data-testid="dashboard-admin-page">
      <Topbar liveBadge />
      <div className="px-6 py-4 bg-white border-b border-gray-200">
        <h1 className="text-lg font-semibold text-gray-900">
          {t.commune} : <span style={{ color: country.palette.primary }}>{communeNom}</span>
        </h1>
        <p className="text-xs text-gray-500 mt-0.5">Vue admin — infrastructures et signalements de votre territoire</p>
      </div>
      <div className="grid grid-cols-4 gap-3 px-6 py-4 bg-white border-b border-gray-200">
        <div className="bg-[#F4F6F9] rounded-xl p-4"><div className="text-2xl font-semibold" style={{ color: country.palette.primary }}>{total}</div><div className="text-xs text-gray-500 mt-1">Infrastructures</div></div>
        <div className="bg-[#F4F6F9] rounded-xl p-4"><div className="text-2xl font-semibold" style={{ color: country.palette.success }}>{pctOk}%</div><div className="text-xs text-gray-500 mt-1">Opérationnel</div></div>
        <div className="bg-[#F4F6F9] rounded-xl p-4"><div className="text-2xl font-semibold" style={{ color: country.palette.danger }}>{alertes}</div><div className="text-xs text-gray-500 mt-1">Signalements ouverts</div></div>
        <div className="bg-[#F4F6F9] rounded-xl p-4"><div className="text-2xl font-semibold" style={{ color: country.palette.accent }}>{signaux.length}</div><div className="text-xs text-gray-500 mt-1">Total signalements</div></div>
      </div>
      <main className="flex-1 overflow-y-auto p-6 grid grid-cols-1 lg:grid-cols-3 gap-5">
        {error && <div className="lg:col-span-3 bg-red-50 border border-red-200 text-red-700 rounded-xl p-4 text-sm">⚠️ {error}</div>}
        <section className="lg:col-span-1 bg-white rounded-xl border border-gray-200 overflow-hidden" data-testid="admin-infra-list">
          <div className="px-4 py-3 border-b border-gray-100 text-sm font-semibold">Infrastructures</div>
          {loading ? (<div className="p-4 text-xs text-gray-500">Chargement…</div>) : infras.length === 0 ? (<div className="p-4 text-xs text-gray-500 text-center">Aucune infrastructure</div>) : (
            <div className="max-h-[60vh] overflow-y-auto">
              {infras.map((inf) => {
                const c = inf.statut === 'operationnel' ? '#1565C0' : inf.statut === 'degrade' ? '#F4A261' : '#C1121F';
                return (
                  <div key={inf.id} className="flex items-center justify-between px-4 py-3 border-b border-gray-50 last:border-b-0">
                    <div>
                      <div className="text-sm font-medium flex items-center gap-1.5">
                        <span>{inf.infrastructure_type?.icone || '🔧'}</span>{inf.nom}
                      </div>
                      <div className="text-[11px] text-gray-500">{inf.infrastructure_type?.nom || '—'}</div>
                    </div>
                    <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold" style={{ background: `${c}22`, color: c }}>{inf.statut}</span>
                  </div>
                );
              })}
            </div>
          )}
        </section>
        <section className="lg:col-span-2 bg-white rounded-xl border border-gray-200 overflow-hidden" data-testid="admin-signal-list">
          <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
            <h3 className="text-sm font-semibold">Signalements</h3>
            <div className="flex gap-1">
              {[{ v: 'all', l: 'Tous' }, { v: 'open', l: 'Ouverts' }, { v: 'resolu', l: 'Résolus' }].map((b) => (
                <button key={b.v} onClick={() => setFilter(b.v)} className={`text-xs px-3 py-1 rounded-full transition ${filter === b.v ? 'text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} style={filter === b.v ? { background: country.palette.primary } : {}} data-testid={`admin-filter-${b.v}`}>{b.l}</button>
              ))}
            </div>
          </div>
          <div className="max-h-[60vh] overflow-y-auto">
            {loading ? (<div className="p-4 text-xs text-gray-500">Chargement…</div>) : filteredSig.length === 0 ? (<div className="p-6 text-xs text-gray-500 text-center">Aucun signalement</div>) : (
              filteredSig.map((s) => {
                const prio = SIGNAL_PRIORITIES.find((p) => p.value === s.priorite);
                const stat = SIGNAL_STATUSES.find((p) => p.value === s.statut);
                return (
                  <div key={s.id} className="px-4 py-3 border-b border-gray-50 last:border-b-0" data-testid={`admin-signal-${s.id}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-gray-900">{s.titre}</div>
                        {s.description && <div className="text-xs text-gray-600 mt-0.5 line-clamp-2">{s.description}</div>}
                        <div className="flex items-center gap-2 mt-1.5">
                          <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold" style={{ background: `${prio?.color}22`, color: prio?.color }}>{prio?.label || s.priorite}</span>
                          <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold" style={{ background: `${stat?.color}22`, color: stat?.color }}>{stat?.label || s.statut}</span>
                          <span className="text-[10px] text-gray-400">{new Date(s.created_at).toLocaleString(country.labels.locale)}</span>
                        </div>
                      </div>
                      <select value={s.statut} onChange={(e) => updateStatut(s.id, e.target.value)} className="text-xs px-2 py-1 rounded border border-gray-200 bg-white" data-testid={`admin-signal-status-${s.id}`}>
                        {SIGNAL_STATUSES.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
                      </select>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </section>
      </main>
    </div>
  );
}
